/**
 * Helpers for the conditional-branching answer setup, shared by the six blocks that have it.
 * Each had its own byte-identical copy (verified by hashing the function bodies, with the locale
 * prefix normalised) — twelve definitions of three functions.
 */
import type { TaskVariable } from '@/types/experience';
import type { BranchCoverageRegion } from '@agavi/blocks';

/**
 * Threshold fields feed straight into `parseThreshold` (a bare `Number(s)` cast) — no formula or
 * variable evaluation, unlike the equation field above them. Filtering every keystroke down to
 * what that cast can parse (digits, one leading `-`, one `.`) means a stray character is rejected
 * as it is typed rather than silently producing a NaN threshold — which `branchMatches` treats as
 * "never matches" and `checkBranchCoverage` drops from its boundary set entirely, so the only
 * symptom used to be a vague "Missing coverage" message nowhere near the broken field.
 */
export function sanitizeThresholdInput(raw: string): string {
  let s = raw.replace(/[^0-9.-]/g, '');
  s = s[0] === '-' ? '-' + s.slice(1).replace(/-/g, '') : s.replace(/-/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  return s;
}

/**
 * A stand-in resolved-variable map for validating the conditional-correctness equation, built from
 * each variable's own declared `min` — NOT from the live-test widget's rolledValues, which only
 * exists while the block is selected. Recomputed from the live `variables` list on every call
 * rather than rolled once into state, so a variable added moments ago resolves immediately.
 */
export function minSampleValues(variables: TaskVariable[]): Record<string, number> {
  const samples: Record<string, number> = {};
  for (const v of variables) {
    if (v.name && v.min != null && !isNaN(v.min)) samples[v.name] = v.min;
  }
  return samples;
}

/**
 * Compact symbol form of a coverage region for the combined "Uncovered conditions: ..." /
 * "Overlapping: ..." lines — a sentence per issue gets awkward once `between`-derived regions are
 * in the mix, so every issue of a given kind is joined into one line instead.
 *
 * Gaps keep the exact operator (`<0`, `>5 to <=6`, `>90`) since the precise boundary is the whole
 * point of a gap warning — it tells the teacher exactly where to add coverage. Overlaps drop that
 * precision for a plain bounded range (`0-1`): knowing roughly where two conditions collide
 * matters more there, and it reads lighter for the rarer case.
 */
export function formatCoverageRegionCompact(
  region: BranchCoverageRegion,
  t: (key: string, opts?: Record<string, unknown>) => string,
  style: 'gap' | 'overlap',
): string {
  if (region.kind === 'point') return `=${region.value}`;
  const { from, to, fromInclusive, toInclusive } = region;
  if (from === null && to === null) return t('blocks.branching.coverageAny');
  if (from !== null && to !== null && style === 'overlap') return `${from}-${to}`;
  // The glyphs, not ASCII digraphs — the operator picker these gaps describe already shows ≤ and ≥
  // (BranchConditionCarousel's OPERATOR_SYMBOL), so `<=` here made the message look like a
  // different notation from the control it is talking about.
  if (from === null) return `${toInclusive ? '≤' : '<'}${to}`;
  if (to === null) return `${fromInclusive ? '≥' : '>'}${from}`;
  return `${fromInclusive ? '≥' : '>'}${from} ${t('blocks.connectors.to')} ${toInclusive ? '≤' : '<'}${to}`;
}

/** How many coverage regions a message names before it summarises the rest. */
const COVERAGE_LIST_CAP = 3;

/**
 * The joined region list a coverage message embeds, capped.
 *
 * Adjacent regions already merge in the engine, so the common wide-domain case arrives as a single
 * range. What survives that is genuinely non-adjacent — alternating coverage, say conditions on 4,
 * 8 and 12 while 2, 6 and 10 are missed — and over a wide domain that is still a list nobody reads.
 * Three named regions plus a count keeps the message actionable: where to start, and how much is
 * left. A pure count would be shorter and useless, since it says nothing about where to look.
 *
 * Replaces the identical `.map(formatCoverageRegionCompact).join(', ')` that all seven branching
 * blocks had written out.
 */
export function formatCoverageList(
  regions: { region: BranchCoverageRegion }[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  style: 'gap' | 'overlap',
): string {
  const shown = regions.slice(0, COVERAGE_LIST_CAP).map(i => formatCoverageRegionCompact(i.region, t, style));
  const hidden = regions.length - shown.length;
  if (hidden > 0) shown.push(t('blocks.branching.coverageMore', { count: hidden }));
  return shown.join(', ');
}
